import React from 'react';
import Link from 'next/link';
import { IResortPathInfo } from '@/api/resort/types';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { cn } from '@/util/cn';

// The linked resort name used by every resort list (region status tables,
// projected opening/closing, the region resort guide, the global snow
// leaderboard), with an optional ", Region" suffix.
//
// Callers decide *whether* the suffix is useful — see
// util/spansMultipleRegions — and this component decides whether we are
// able to render it at all. The suffix comes from `region_en`, the
// untranslated English region name, so it stays gated to en-US; the other
// twelve locales would otherwise get an English region name glued to a
// translated resort name. Lifting that gate means threading the translated
// region title (public/index/regions-{lang}.json, keyed by region_id)
// through the page's server fetcher and preferring it here.
export type ResortNameLinkProps = {
  resort: Pick<IResortPathInfo, 'title' | 'region_en'>;
  href: string;
  showRegion?: boolean;
  className?: string;
};

const canRenderRegion = () => getLocale() === ELocale.EN_US;

export const ResortNameLink = ({
  resort, href, showRegion = false, className,
}: ResortNameLinkProps) => {
  const region = resort.region_en?.trim();
  const withRegion = showRegion && !!region && canRenderRegion();

  return (
    <>
      <Link
        href={href}
        className={cn('hover:text-primary hover:underline', className)}
      >
        {resort.title}
      </Link>
      {withRegion && (
        <span className="font-normal text-muted-foreground">
          {`, ${region}`}
        </span>
      )}
    </>
  );
};
